// crewbit-admin/src/lib/recurringInvoiceMutations.ts
//
// Phase B.3.4 — Money Module RPC-routed recurring invoice mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_DATA_v1.md §5.X (M-A4-017 + M-A4-018, Session 101).
// No mobile parallel — recurring invoices are an admin-only surface today.
//
// Plain async functions, not a hook — they don't read or write React state.
// Identity (created_by, business_id) resolves server-side via
// get_public_user_id() / get_user_business_id() inside the RPC bodies; the
// client never passes them. Subtotal / tax_amount / total are computed
// server-side from line_items (cents) + tax_rate; clients should not pre-compute.

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { toRpcLineItems, type AdminLineItem } from './invoiceMutations';

export interface CreateRecurringInvoiceArgs {
  businessId: string;
  lineItems: AdminLineItem[];
  /** Enum: weekly | biweekly | monthly | quarterly | annually. */
  frequency: string;
  /** ISO date 'YYYY-MM-DD'. RPC enforces >= current_date. */
  nextInvoiceDate: string;
  /** FK to clients row when one exists. NULL → use clientName/clientEmail. */
  clientId?: string | null;
  /** Required when clientId is null (RPC errors with CLIENT_REQUIRED otherwise). */
  clientName?: string | null;
  clientEmail?: string | null;
  /** Fraction (0–1). NULL → falls back to business_financial_settings.hst_rate or 0.13. */
  taxRate?: number | null;
  isActive?: boolean | null;
  autoSend?: boolean | null;
  notes?: string | null;
  /** Optional — recurring_invoices schema doesn't carry idempotency_key today; param accepted for forward-compat. */
  idempotencyKey?: string | null;
}

export type CreateRecurringInvoiceResult = MoneyRpcResult<{
  recurring_invoice_id: string;
  subtotal: number;
  tax_amount: number;
  total: number;
}>;

export async function createRecurringInvoice(
  args: CreateRecurringInvoiceArgs,
): Promise<CreateRecurringInvoiceResult> {
  return callMoneyRpc('create_recurring_invoice', {
    p_business_id: args.businessId,
    p_line_items: toRpcLineItems(args.lineItems),
    p_frequency: args.frequency,
    p_next_invoice_date: args.nextInvoiceDate,
    p_client_id: args.clientId,
    p_client_name: args.clientName,
    p_client_email: args.clientEmail,
    p_tax_rate: args.taxRate,
    p_is_active: args.isActive,
    p_auto_send: args.autoSend,
    p_notes: args.notes,
    p_idempotency_key: args.idempotencyKey,
  });
}

export interface UpdateRecurringInvoiceArgs {
  recurringInvoiceId: string;
  lineItems?: AdminLineItem[] | null;
  frequency?: string | null;
  nextInvoiceDate?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  taxRate?: number | null;
  isActive?: boolean | null;
  autoSend?: boolean | null;
  notes?: string | null;
  /** ISO timestamp string. Used by markGenerated wrapper. */
  lastGeneratedAt?: string | null;
}

export type UpdateRecurringInvoiceResult = MoneyRpcResult<{
  recurring_invoice_id: string;
  updated_fields: string[];
}>;

export async function updateRecurringInvoice(
  args: UpdateRecurringInvoiceArgs,
): Promise<UpdateRecurringInvoiceResult> {
  return callMoneyRpc('update_recurring_invoice', {
    p_recurring_invoice_id: args.recurringInvoiceId,
    p_line_items: args.lineItems ? toRpcLineItems(args.lineItems) : null,
    p_frequency: args.frequency,
    p_next_invoice_date: args.nextInvoiceDate,
    p_client_id: args.clientId,
    p_client_name: args.clientName,
    p_client_email: args.clientEmail,
    p_tax_rate: args.taxRate,
    p_is_active: args.isActive,
    p_auto_send: args.autoSend,
    p_notes: args.notes,
    p_last_generated_at: args.lastGeneratedAt,
  });
}

/**
 * Thin wrapper of `updateRecurringInvoice` — sets last_generated_at to now()
 * and advances next_invoice_date. Caller computes the next date from frequency
 * (the RPC stores the value but doesn't compute the schedule itself).
 */
export async function markGenerated(
  recurringInvoiceId: string,
  nextInvoiceDate: string,
): Promise<UpdateRecurringInvoiceResult> {
  return updateRecurringInvoice({
    recurringInvoiceId,
    lastGeneratedAt: new Date().toISOString(),
    nextInvoiceDate,
  });
}

/** Thin wrapper — toggle is_active. */
export async function setActive(
  recurringInvoiceId: string,
  isActive: boolean,
): Promise<UpdateRecurringInvoiceResult> {
  return updateRecurringInvoice({ recurringInvoiceId, isActive });
}

/** Thin wrapper — toggle auto_send. */
export async function setAutoSend(
  recurringInvoiceId: string,
  autoSend: boolean,
): Promise<UpdateRecurringInvoiceResult> {
  return updateRecurringInvoice({ recurringInvoiceId, autoSend });
}
